import { useEffect, useState, useMemo } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { formatINR } from "../lib/utils-format";
import {
    Users, IndianRupee, Award, Wallet, TrendingUp,
    Search, ChevronDown, ChevronUp, FileCheck, Clock, AlertCircle, CheckCircle2,
    ArrowDownLeft, ArrowDownRight,
} from "lucide-react";

const KYC_BADGE = {
    approved: { label: "Verified",  cls: "bg-emerald-50 text-emerald-700",  icon: CheckCircle2 },
    pending:  { label: "Pending",   cls: "bg-amber-50 text-amber-600",      icon: Clock        },
    none:     { label: "No KYC",    cls: "bg-slate-50 text-slate-500",      icon: AlertCircle  },
};

const SORT_COLS = [
    { key: "full_name",        label: "Name"          },
    { key: "direct_bonus",     label: "Direct"        },
    { key: "pairing_bonus",    label: "Pairing"       },
    { key: "travel_fund",      label: "Travel Fund"   },
    { key: "available_balance",label: "Available"     },
    { key: "total_earned",     label: "Total Earned"  },
    { key: "activated_at",     label: "Joined"        },
];

function SortIcon({ col, sortBy, sortDir }) {
    if (sortBy !== col) return null;
    return sortDir === "asc"
        ? <ChevronUp className="w-3 h-3 inline ml-0.5" />
        : <ChevronDown className="w-3 h-3 inline ml-0.5" />;
}

export default function AdminMemberEarnings() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,  setSearch]  = useState("");
    const [sortBy,  setSortBy]  = useState("activated_at");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/admin/bonus-overview");
                setMembers(res.data);
            } catch (_) {}
            finally { setLoading(false); }
        })();
    }, []);

    // ── Aggregate totals ────────────────────────────────────────────────────────
    const totals = useMemo(() => members.reduce((acc, m) => ({
        direct:    acc.direct    + m.direct_bonus,
        pairing:   acc.pairing   + m.pairing_bonus,
        tf:        acc.tf        + m.travel_fund_total,
        available: acc.available + m.available_balance,
        pending:   acc.pending   + m.pending_balance,
        withdrawn: acc.withdrawn + m.withdrawn,
        earned:    acc.earned    + m.total_earned,
    }), { direct: 0, pairing: 0, tf: 0, available: 0, pending: 0, withdrawn: 0, earned: 0 }), [members]);

    // ── Filter + sort ───────────────────────────────────────────────────────────
    const sorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        let rows = q
            ? members.filter(m =>
                m.full_name.toLowerCase().includes(q) ||
                m.email.toLowerCase().includes(q) ||
                m.referral_code.toLowerCase().includes(q)
              )
            : [...members];

        rows.sort((a, b) => {
            const av = a[sortBy] ?? "";
            const bv = b[sortBy] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return rows;
    }, [members, search, sortBy, sortDir]);

    function toggleSort(col) {
        if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortBy(col); setSortDir("desc"); }
    }

    function Th({ col, children, className = "" }) {
        return (
            <th
                onClick={() => toggleSort(col)}
                className={`pb-3 text-[10px] font-extrabold uppercase tracking-widest cursor-pointer select-none whitespace-nowrap hover:text-[#1B2B6B] transition-colors ${className}`}
            >
                {children}<SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
            </th>
        );
    }

    return (
        <AppShell admin>
            <div className="space-y-6">

                {/* ── Header ── */}
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="font-display font-black text-3xl tracking-tighter text-[#1B2B6B] mt-1">
                        Member Earnings
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Income paid out to every member — direct bonuses, pairing bonuses, travel fund, and wallet balances.
                    </p>
                </div>

                {/* ── 5 summary cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                        {
                            label: "Direct Bonuses Paid",
                            value: formatINR(totals.direct),
                            sub:   `${members.filter(m => m.direct_bonus > 0).length} members`,
                            icon:  IndianRupee,
                            color: "text-emerald-700", bg: "bg-emerald-50",  border: "border-l-emerald-500",
                        },
                        {
                            label: "Pairing Bonuses Paid",
                            value: formatINR(totals.pairing),
                            sub:   `${members.filter(m => m.pairing_bonus > 0).length} members`,
                            icon:  Award,
                            color: "text-purple-700",  bg: "bg-purple-50",   border: "border-l-purple-500",
                        },
                        {
                            label: "Travel Fund Credited",
                            value: formatINR(totals.tf),
                            sub:   `${members.filter(m => m.travel_fund_total > 0).length} members`,
                            icon:  TrendingUp,
                            color: "text-blue-700",    bg: "bg-blue-50",     border: "border-l-blue-500",
                        },
                        {
                            label: "Net in Wallets",
                            value: formatINR(totals.available),
                            sub:   `${formatINR(totals.pending)} pending`,
                            icon:  Wallet,
                            color: "text-[#1B2B6B]",  bg: "bg-[#EEF4FB]",   border: "border-l-[#2172E5]",
                        },
                        {
                            label: "Total Paid Out",
                            value: formatINR(totals.earned),
                            sub:   `${members.length} members`,
                            icon:  Users,
                            color: "text-slate-800",   bg: "bg-white",       border: "border-l-slate-400",
                        },
                    ].map(({ label, value, sub, icon: Icon, color, bg, border }) => (
                        <div key={label} className={`${bg} rounded-3xl p-5 border-l-4 ${border} shadow-sm`}>
                            <div className="flex items-center gap-2 mb-2">
                                <Icon className={`w-3.5 h-3.5 ${color}`} />
                                <span className={`text-[10px] font-bold uppercase tracking-wider opacity-70 ${color}`}>{label}</span>
                            </div>
                            <div className={`text-xl font-extrabold ${color}`}>{value}</div>
                            <div className="text-[10px] text-slate-400 mt-1">{sub}</div>
                        </div>
                    ))}
                </div>

                {/* ── Table card ── */}
                <div className="bg-white rounded-3xl shadow-sm p-6">

                    {/* Table header bar */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-5">
                        <div>
                            <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">All Members</span>
                            <h3 className="font-display font-bold text-lg text-slate-900 mt-1">
                                Individual Earnings Breakdown
                                {!loading && (
                                    <span className="ml-2 text-sm font-normal text-slate-400">
                                        ({sorted.length} of {members.length})
                                    </span>
                                )}
                            </h3>
                        </div>
                        <div className="relative w-full sm:w-64 flex-shrink-0">
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search name, email, ID…"
                                className="w-full pl-9 pr-4 py-2 bg-[#EEF4FB] border border-transparent rounded-full text-xs focus:outline-none focus:border-[#2563EB] transition-colors"
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-16 text-center text-slate-400 text-sm animate-pulse">Loading member data…</div>
                    ) : sorted.length === 0 ? (
                        <div className="py-16 text-center text-slate-400 text-sm">No members match your search.</div>
                    ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead>
                                <tr className="border-b-2 border-slate-100 text-slate-400">
                                    <Th col="full_name"         className="pr-3">Member</Th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap hidden sm:table-cell">Wing</th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap hidden md:table-cell">KYC</th>
                                    <Th col="activated_at"      className="hidden lg:table-cell">Joined</Th>
                                    <Th col="direct_bonus"      className="text-right text-emerald-500 pr-3">Direct Bonus</Th>
                                    <Th col="pairing_bonus"     className="text-right text-purple-500 pr-3 hidden md:table-cell">Pairing</Th>
                                    <Th col="travel_fund"       className="text-right text-blue-500 pr-3 hidden lg:table-cell">Travel Fund</Th>
                                    <Th col="available_balance" className="text-right text-[#10B1B6] pr-3">Available</Th>
                                    <Th col="total_earned"      className="text-right pr-3 hidden sm:table-cell">Total Earned</Th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase tracking-widest text-right text-red-400 hidden xl:table-cell">Withdrawn</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {sorted.map((m) => {
                                    const kyc = KYC_BADGE[m.kyc_status] ?? KYC_BADGE.none;
                                    const KycIcon = kyc.icon;
                                    return (
                                        <tr key={m.id} className="hover:bg-slate-50/60 transition-colors">

                                            {/* Member */}
                                            <td className="py-3 pr-3 align-middle">
                                                <div className="font-semibold text-slate-800 truncate max-w-[140px]">{m.full_name}</div>
                                                <div className="text-[10px] font-mono text-slate-400">{m.referral_code}</div>
                                                <div className="text-[10px] text-slate-300 truncate max-w-[140px] hidden sm:block">{m.email}</div>
                                            </td>

                                            {/* Wing + team counts */}
                                            <td className="py-3 pr-3 align-middle hidden sm:table-cell">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold w-fit ${
                                                        m.side === "L" ? "bg-teal-50 text-teal-700" : m.side === "R" ? "bg-pink-50 text-pink-700" : "bg-slate-50 text-slate-400"
                                                    }`}>
                                                        {m.side === "L" ? <ArrowDownLeft className="w-2.5 h-2.5" /> : m.side === "R" ? <ArrowDownRight className="w-2.5 h-2.5" /> : null}
                                                        {m.side || "—"}
                                                    </span>
                                                    <span className="text-[9px] text-slate-300 whitespace-nowrap">
                                                        {m.left_count}L · {m.right_count}R
                                                    </span>
                                                </div>
                                            </td>

                                            {/* KYC */}
                                            <td className="py-3 pr-3 align-middle hidden md:table-cell">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${kyc.cls}`}>
                                                    <KycIcon className="w-2.5 h-2.5" />
                                                    {kyc.label}
                                                </span>
                                            </td>

                                            {/* Joined */}
                                            <td className="py-3 pr-3 text-slate-400 whitespace-nowrap align-middle hidden lg:table-cell">
                                                {m.activated_at
                                                    ? new Date(m.activated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                                    : "—"}
                                            </td>

                                            {/* Direct Bonus */}
                                            <td className="py-3 pr-3 text-right align-middle">
                                                <span className={`font-mono font-bold ${m.direct_bonus > 0 ? "text-emerald-600" : "text-slate-200"}`}>
                                                    {m.direct_bonus > 0 ? formatINR(m.direct_bonus) : "—"}
                                                </span>
                                            </td>

                                            {/* Pairing */}
                                            <td className="py-3 pr-3 text-right align-middle hidden md:table-cell">
                                                <span className={`font-mono font-bold ${m.pairing_bonus > 0 ? "text-purple-600" : "text-slate-200"}`}>
                                                    {m.pairing_bonus > 0 ? formatINR(m.pairing_bonus) : "—"}
                                                </span>
                                            </td>

                                            {/* Travel Fund */}
                                            <td className="py-3 pr-3 text-right align-middle hidden lg:table-cell">
                                                <div className={`font-mono font-bold ${m.travel_fund_total > 0 ? "text-blue-600" : "text-slate-200"}`}>
                                                    {m.travel_fund_total > 0 ? formatINR(m.travel_fund_total) : "—"}
                                                </div>
                                                {m.travel_fund_total > m.travel_fund && (
                                                    <div className="text-[9px] text-amber-500 font-semibold whitespace-nowrap">
                                                        {formatINR(m.travel_fund)} available
                                                    </div>
                                                )}
                                            </td>

                                            {/* Available */}
                                            <td className="py-3 pr-3 text-right align-middle">
                                                <div className={`font-mono font-extrabold text-sm ${m.available_balance > 0 ? "text-[#10B1B6]" : "text-slate-300"}`}>
                                                    {formatINR(m.available_balance)}
                                                </div>
                                                {m.pending_balance > 0 && (
                                                    <div className="text-[9px] text-amber-500 font-semibold whitespace-nowrap">
                                                        +{formatINR(m.pending_balance)} pending
                                                    </div>
                                                )}
                                            </td>

                                            {/* Total Earned */}
                                            <td className="py-3 pr-3 text-right align-middle hidden sm:table-cell">
                                                <span className={`font-mono font-extrabold text-sm ${m.total_earned > 0 ? "text-[#1B2B6B]" : "text-slate-200"}`}>
                                                    {m.total_earned > 0 ? formatINR(m.total_earned) : "—"}
                                                </span>
                                            </td>

                                            {/* Withdrawn */}
                                            <td className="py-3 text-right align-middle hidden xl:table-cell">
                                                <span className={`font-mono ${m.withdrawn > 0 ? "text-red-500 font-bold" : "text-slate-200"}`}>
                                                    {m.withdrawn > 0 ? `−${formatINR(m.withdrawn)}` : "—"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-slate-200 bg-slate-50/60">
                                    <td className="pt-3 pb-2 pr-3 text-xs font-bold text-slate-600">
                                        {sorted.length} members
                                    </td>
                                    <td className="hidden sm:table-cell" />
                                    <td className="hidden md:table-cell" />
                                    <td className="hidden lg:table-cell" />
                                    <td className="pt-3 pb-2 pr-3 text-right font-extrabold text-emerald-700 font-mono">
                                        {formatINR(sorted.reduce((s, m) => s + m.direct_bonus, 0))}
                                    </td>
                                    <td className="pt-3 pb-2 pr-3 text-right font-extrabold text-purple-700 font-mono hidden md:table-cell">
                                        {formatINR(sorted.reduce((s, m) => s + m.pairing_bonus, 0))}
                                    </td>
                                    <td className="pt-3 pb-2 pr-3 text-right font-extrabold text-blue-700 font-mono hidden lg:table-cell">
                                        {formatINR(sorted.reduce((s, m) => s + m.travel_fund_total, 0))}
                                    </td>
                                    <td className="pt-3 pb-2 pr-3 text-right font-extrabold text-[#10B1B6] font-mono">
                                        {formatINR(sorted.reduce((s, m) => s + m.available_balance, 0))}
                                    </td>
                                    <td className="pt-3 pb-2 pr-3 text-right font-extrabold text-[#1B2B6B] font-mono hidden sm:table-cell">
                                        {formatINR(sorted.reduce((s, m) => s + m.total_earned, 0))}
                                    </td>
                                    <td className="pt-3 pb-2 text-right font-extrabold text-red-500 font-mono hidden xl:table-cell">
                                        {totals.withdrawn > 0 ? `−${formatINR(sorted.reduce((s, m) => s + m.withdrawn, 0))}` : "—"}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    )}
                </div>

            </div>
        </AppShell>
    );
}
